import html2Canvas from 'html2canvas';
import JsPDF from 'jspdf';

// 需要保护的元素类型（避免被分页切断）
const PROTECTED_ELEMENTS = [
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6', // 标题
];

const PROTECTED_CLASSES = [
  ['form-group', 'row'], // 表单项
  'roo-divider', // 分隔线
  'preview-label-wrap',
  'h2-title-wrap',
  'control-label',
  'ql-editor',
];

/**
 * 判断元素是否需要保护（避免被分页切断）
 * @param {HTMLElement} element - 元素
 * @returns {boolean} 是否需要保护
 */
function isProtectedElement(element) {
  // 检查标签名
  if (PROTECTED_ELEMENTS.includes(element.tagName)) {
    return true;
  }

  // 检查类名（支持单个类名和类名组合）
  return PROTECTED_CLASSES.some((classItem) => {
    if (Array.isArray(classItem)) {
      // 数组表示必须同时包含的class组合
      return classItem.every((className) => element.classList.contains(className));
    } else {
      // 字符串表示单个class
      return element.classList.contains(classItem);
    }
  });
}

/**
 * 添加调试边框，显示所有不可分割的元素
 * @param {HTMLElement} container - 容器元素
 * @returns {Array} 被标记的保护元素数组
 */
function addDebugBorders(container) {
  const protectedElements = [];
  const processedElements = new Set(); // 避免重复处理

  // 创建调试样式
  const debugStyle = document.createElement('style');
  debugStyle.id = 'debug-protected-elements';
  debugStyle.textContent = `
    .debug-protected-element {
      background: grey;
      position: relative !important;
    }
    .debug-protected-element::before {
      content: attr(data-debug-info);
      position: absolute;
      top: -20px;
      left: 0;
      background: red;
      color: white;
      font-size: 10px;
      padding: 2px 4px;
      z-index: 9999;
      white-space: nowrap;
    }
  `;

  // 移除旧的调试样式
  const oldStyle = document.getElementById('debug-protected-elements');
  if (oldStyle) {
    oldStyle.remove();
  }

  document.head.appendChild(debugStyle);

  // 使用TreeWalker遍历所有元素
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_ELEMENT,
    {
      acceptNode: (node) => {
        // 如果父元素已经被标记为保护元素，跳过子元素
        let parent = node.parentElement;
        while (parent && parent !== container) {
          if (processedElements.has(parent)) {
            return NodeFilter.FILTER_REJECT;
          }
          parent = parent.parentElement;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    },
  );

  let element;
  let elementIndex = 0;

  // eslint-disable-next-line no-cond-assign
  while (element = walker.nextNode()) {
    if (isProtectedElement(element)) {
      // 标记为已处理
      processedElements.add(element);

      // 添加调试样式
      element.classList.add('debug-protected-element');

      // 添加调试信息
      const debugInfo = `${elementIndex + 1}. ${element.tagName}${element.className ? `.${element.className.split(' ')[0]}` : ''}`;
      element.setAttribute('data-debug-info', debugInfo);

      // 记录元素信息
      const rect = element.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // 🔧 修复坐标计算：统一使用getBoundingClientRect方法，与getElementPositions保持一致
      const basicTopPosition = rect.top - containerRect.top + container.scrollTop;

      // 统一使用getBoundingClientRect方法，确保与getElementPositions坐标系一致
      const topPosition = basicTopPosition;

      protectedElements.push({
        element,
        index: elementIndex + 1,
        tagName: element.tagName,
        className: element.className,
        top: topPosition,
        height: rect.height,
        debugInfo,
      });

      elementIndex += 1;
    }
  }

  console.log(`总共找到 ${protectedElements.length} 个不可分割的保护元素`);
  return protectedElements;
}

/**
 * 获取容器内所有元素的位置信息
 * @param {HTMLElement} container - 容器元素
 * @returns {Array} 元素位置信息数组
 */
function getElementPositions(container) {
  const elements = [];
  const containerRect = container.getBoundingClientRect();

  // 使用TreeWalker遍历所有元素
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_ELEMENT,
    {
      acceptNode: (node) => {
        const rect = node.getBoundingClientRect();
        // 过滤掉不可见或很小的元素
        return rect.height > 5 && rect.width > 5
          ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
      },
    },
  );

  let element;
  // eslint-disable-next-line no-cond-assign
  while (element = walker.nextNode()) {
    const elementRect = element.getBoundingClientRect();

    const topPosition = Number(elementRect.top) - Number(containerRect.top) + Number(container.scrollTop);

    // 🔧 预先获取padding信息，避免在分页检查时重复计算
    const computedStyle = window.getComputedStyle(element);
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;

    elements.push({
      element,
      top: topPosition,
      bottom: topPosition + Number(elementRect.height),
      height: Number(elementRect.height),
      tagName: element.tagName,
      className: element.className,
      isProtected: isProtectedElement(element),
      paddingTop,
      paddingBottom,
    });
  }

  return elements.sort((a, b) => a.top - b.top);
}

/**
 * 检查保护元素是否能完整放在当前页
 * @param {number} currentY - 当前页起始位置
 * @param {number} pageHeight - 页面高度
 * @param {Array} elements - 元素数组
 * @returns {Object} 检查结果 {canFit: boolean, adjustedBreakPoint: number, conflictElement: Object}
 */
function checkProtectedElementsFit(currentY, pageHeight, elements) {
  // 参数验证
  if (typeof currentY !== 'number' || currentY < 0) {
    console.warn('Invalid currentY parameter:', currentY);
    return { canFit: true, adjustedBreakPoint: currentY + pageHeight };
  }
  if (typeof pageHeight !== 'number' || pageHeight <= 0) {
    console.warn('Invalid pageHeight parameter:', pageHeight);
    return { canFit: true, adjustedBreakPoint: currentY + 100 };
  }
  if (!Array.isArray(elements)) {
    console.warn('Invalid elements parameter:', elements);
    return { canFit: true, adjustedBreakPoint: currentY + pageHeight };
  }

  const pageEndY = currentY + pageHeight;

  // 找到与当前页有重叠的所有保护元素
  const elementsInPage = elements.filter((el) => el.isProtected
    && !(el.bottom <= currentY || el.top >= pageEndY));

  // 检查每个保护元素是否能完整放在当前页
  const conflictElement = elementsInPage.find((element) => {
    const elementHeight = element.bottom - element.top;

    // 如果元素太大，超过页面高度的80%，需要特殊处理
    if (elementHeight > pageHeight * 0.8) {
      console.log('发现超大元素，需要特殊处理:', {
        element: element.tagName,
        className: element.className,
        height: elementHeight,
        maxHeight: pageHeight * 0.8,
        elementTop: element.top,
        elementBottom: element.bottom,
        strategy: elementHeight > pageHeight * 1.5 ? '允许分割' : '创建专门页面',
      });

      // 如果元素超过1.5倍页面高度，允许分割
      if (elementHeight > pageHeight * 1.5) {
        return false; // 跳过此元素，允许被分割
      }

      // 否则尝试为此元素创建专门的页面
      return true; // 仍然尝试保护此元素
    }

    // 检查元素是否会被当前页切断
    if (element.bottom > pageEndY) {
      // 🔧 检查是否只是在padding区域被切断，如果是则允许
      if (element.paddingTop !== undefined && element.paddingBottom !== undefined) {
        // 计算内容区域的范围（排除padding）
        const contentTop = element.top + element.paddingTop;
        const contentBottom = element.bottom - element.paddingBottom;

        // 如果内容区域完全在当前页内，则允许在padding区域被切断
        if (contentBottom <= pageEndY) {
          console.log('✅ 元素在padding区域被切断，允许:', {
            element: element.tagName,
            className: element.className,
            elementRange: `${element.top} - ${element.bottom}`,
            contentRange: `${contentTop} - ${contentBottom}`,
            paddingTop: element.paddingTop,
            paddingBottom: element.paddingBottom,
            pageEndY,
            说明: '内容区域完全在当前页内，只有padding被切断',
          });
          return false; // 不需要保护，允许在padding区域切断
        }
      }

      console.log('发现被切断的保护元素，需要移到下一页:', {
        element: element.tagName,
        className: element.className,
        elementTop: element.top,
        elementBottom: element.bottom,
        pageEndY,
        elementHeight,
      });
      return true; // 找到冲突元素
    }

    // 🔧 检查元素底部是否正好在分页点上，避免Canvas drawImage边界问题
    const boundaryDiff = Math.abs(element.bottom - pageEndY);

    if (boundaryDiff < 0.1) {
      console.log('🔧 发现元素底部正好在分页点上，需要调整避免Canvas切片问题:', {
        element: element.tagName,
        className: element.className,
        elementTop: element.top,
        elementBottom: element.bottom,
        pageEndY,
        差值: boundaryDiff,
      });
      return true; // 找到边界重合元素
    }

    return false;
  });

  if (conflictElement) {
    let adjustedBreakPoint;

    // 检查冲突类型：元素被切断 vs 元素底部正好在分页点上
    if (conflictElement.bottom > pageEndY) {
      // 元素被切断，将分页点调整到元素开始位置
      adjustedBreakPoint = Math.max(currentY + 50, conflictElement.top);
    } else if (Math.abs(conflictElement.bottom - pageEndY) < 0.1) {
      // 元素底部正好在分页点上，向前调整几个像素避免Canvas切片问题
      const BOUNDARY_SAFETY = 3; // 3像素安全边距，避免Canvas切片边界问题
      adjustedBreakPoint = conflictElement.bottom - BOUNDARY_SAFETY;
      console.log('🔧 检测到元素底部与分页点重合，调整分页点避免Canvas切片问题:', {
        conflictElement: {
          tagName: conflictElement.tagName,
          range: `${conflictElement.top} - ${conflictElement.bottom}`,
        },
        原始分页点: pageEndY,
        调整后分页点: adjustedBreakPoint,
        安全边距: BOUNDARY_SAFETY,
      });
    } else {
      // 其他情况，使用默认调整
      adjustedBreakPoint = Math.max(currentY + 50, conflictElement.top);
    }

    console.log('🔍 初始分页点调整:', {
      currentY,
      pageHeight,
      originalPageEndY: pageEndY,
      conflictElement: {
        tagName: conflictElement.tagName,
        range: `${conflictElement.top} - ${conflictElement.bottom}`,
      },
      initialAdjustment: adjustedBreakPoint,
    });

    // 🔧 检查调整后的分页点是否与其他保护元素的底部重合
    const BOUNDARY_TOLERANCE_1 = 0.1;
    const elementsAtAdjustedBoundary = elements.filter(el =>
      el.isProtected && Math.abs(el.bottom - adjustedBreakPoint) < BOUNDARY_TOLERANCE_1
    );

    if (elementsAtAdjustedBoundary.length > 0) {
      console.log('🔧 检测到调整后分页点与其他元素底部重合，需要进一步调整:', {
        adjustedBreakPoint,
        elementsAtBoundary: elementsAtAdjustedBoundary.map(el => ({
          tagName: el.tagName,
          range: `${el.top} - ${el.bottom}`,
          差值: Math.abs(el.bottom - adjustedBreakPoint),
        })),
      });

      // 将分页点向前调整3像素，避免Canvas切片问题
      const BOUNDARY_SAFETY = 3;
      adjustedBreakPoint = adjustedBreakPoint - BOUNDARY_SAFETY;

      console.log('🔧 应用边界安全调整:', {
        原始调整后分页点: adjustedBreakPoint + BOUNDARY_SAFETY,
        最终分页点: adjustedBreakPoint,
        安全边距: BOUNDARY_SAFETY,
      });
    }

    const SAFE_MARGIN = 2; // 安全边距，避免浮点数精度问题
    let iterationCount = 0;
    const maxIterations = 10; // 防止无限循环

    console.log('? 开始递归调整分页点，初始状态:', {
      initialAdjustment: adjustedBreakPoint,
      safeMargin: SAFE_MARGIN,
      maxIterations,
    });

    // 递归调整分页点，直到没有元素被影响
    while (iterationCount < maxIterations) {
      console.log(`🔄 开始第${iterationCount + 1}轮检查，当前分页点:`, adjustedBreakPoint);

      // 找到所有可能受影响的保护元素
      const affectedElements = elements.filter((el) => {
        if (!el.isProtected) return false;

        // 只检查元素是否会被新的分页点分割
        // 不再检查边界重合，因为边界重合不会导致元素被分割
        const wouldBeSplit = el.top < adjustedBreakPoint && el.bottom > adjustedBreakPoint;

        // 只保护真正会被分割的元素
        return wouldBeSplit;
      });

      console.log(`🔍 第${iterationCount + 1}轮受影响元素检查结果:`, {
        adjustedBreakPoint,
        affectedElementsCount: affectedElements.length,
        affectedElements: affectedElements.map(el => ({
          tagName: el.tagName,
          range: `${el.top} - ${el.bottom}`,
          wouldBeSplit: el.top < adjustedBreakPoint && el.bottom > adjustedBreakPoint,
          elementStatus: el.bottom <= adjustedBreakPoint ? '完全在页面内' :
            el.top >= adjustedBreakPoint ? '完全在页面外' : '跨页分割',
        })),
      });

      if (affectedElements.length === 0) {
        // 没有受影响的元素，调整完成
        console.log(`✅ 第${iterationCount + 1}轮检查完成，没有受影响的元素，退出循环`);
        break;
      }

      // 找到最早的受影响元素，将分页点调整到它的顶部
      const earliestAffectedElement = affectedElements.reduce((earliest, current) =>
        current.top < earliest.top ? current : earliest
      );

      // 直接使用元素顶部作为分页点，不减去安全边距，避免连锁反应
      const newAdjustment = earliestAffectedElement.top;

      console.log(`检测到元素冲突，第${iterationCount + 1}轮调整分页点:`, {
        currentAdjustment: adjustedBreakPoint,
        affectedElementsCount: affectedElements.length,
        earliestAffectedElement: {
          tagName: earliestAffectedElement.tagName,
          range: `${earliestAffectedElement.top} - ${earliestAffectedElement.bottom}`,
        },
        newAdjustment,
        safeMargin: SAFE_MARGIN,
      });

      // 确保调整后的分页点不会太小
      if (newAdjustment - currentY < 50) {
        adjustedBreakPoint = currentY + 50;
        console.log('调整后页面太小，使用最小页面高度:', adjustedBreakPoint);
        break;
      }

      adjustedBreakPoint = newAdjustment;
      console.log(`✅ 分页点已更新为:`, adjustedBreakPoint);
      iterationCount++;
    }

    if (iterationCount >= maxIterations) {
      console.warn('分页点调整达到最大迭代次数，停止调整');
    }

    // 🔧 递归调整完成后，检查最终分页点是否与保护元素底部重合
    const BOUNDARY_TOLERANCE = 0.1;
    const elementsAtFinalBoundary = elements.filter(el =>
      el.isProtected && Math.abs(el.bottom - adjustedBreakPoint) < BOUNDARY_TOLERANCE
    );

    if (elementsAtFinalBoundary.length > 0) {
      console.log('✅ 递归调整完成后检测到最终分页点与保护元素底部重合，这是理想状态:', {
        finalBreakPoint: adjustedBreakPoint,
        elementsAtBoundary: elementsAtFinalBoundary.map(el => ({
          tagName: el.tagName,
          range: `${el.top} - ${el.bottom}`,
          差值: Math.abs(el.bottom - adjustedBreakPoint),
          状态: '完全在当前页内',
        })),
        说明: '分页点正好在保护元素底部，元素完全在当前页内，无需调整',
      });

    }

    return {
      canFit: false,
      adjustedBreakPoint,
      conflictElement,
    };
  }

  return {
    canFit: true,
    adjustedBreakPoint: pageEndY,
    conflictElement: null,
  };
}

/**
 * 分析保护元素在各页面的分布情况
 * @param {Array} protectedElements - 保护元素数组
 * @param {Array} pagePositions - 分页位置数组
 * @returns {Object} 元素分页映射
 */
function analyzeElementPageDistribution(protectedElements, pagePositions) {
  const elementPageMap = new Map();
  const pageElementMap = new Map();

  // 初始化页面元素映射
  pagePositions.forEach((page, index) => {
    pageElementMap.set(index + 1, []);
  });

  protectedElements.forEach((element) => {
    const elementStart = Number(element.top);
    const elementEnd = Number(element.top) + Number(element.height);
    const belongsToPages = [];

    pagePositions.forEach((page, pageIndex) => {
      const pageStart = Number(page.startY);
      const pageEnd = Number(page.startY) + Number(page.height);

      // 检查元素是否与当前页有重叠
      const hasOverlap = !(elementEnd <= pageStart || elementStart >= pageEnd);

      if (hasOverlap) {
        belongsToPages.push(pageIndex + 1);
        pageElementMap.get(pageIndex + 1).push({
          index: element.index,
          tagName: element.tagName,
          className: element.className,
          elementStart,
          elementEnd,
          pageStart,
          pageEnd,
          overlapStart: Math.max(elementStart, pageStart),
          overlapEnd: Math.min(elementEnd, pageEnd),
        });
      }
    });

    elementPageMap.set(element.index, belongsToPages);
  });

  // 输出每页包含的元素
  pageElementMap.forEach((elements, pageNum) => {
    console.log(`📄 第${pageNum}页包含的保护元素:`, {
      count: elements.length,
      elements: elements.map((el) => `${el.index}(${el.tagName})`),
    });
  });

  return { elementPageMap, pageElementMap };
}

/**
 * 计算智能分页位置
 * @param {number} contentHeight - 内容总高度
 * @param {number} safePageHeight - 安全页面高度
 * @param {Array} elements - 元素数组
 * @returns {Array} 分页位置数组
 */
function calculateSmartPageBreaks(contentHeight, safePageHeight, elements) {
  const pagePositions = [];
  let currentY = 0;
  let pageIndex = 0;

  while (currentY < contentHeight) {
    const remainingHeight = contentHeight - currentY;
    let pageHeight = Math.min(safePageHeight, remainingHeight);

    if (pageHeight <= 0) break;

    // 检查保护元素是否能完整放在当前页
    const checkResult = checkProtectedElementsFit(currentY, pageHeight, elements);

    let breakPoint = currentY + pageHeight;

    // 如果有保护元素被切断，调整分页点
    if (!checkResult.canFit && checkResult.adjustedBreakPoint) {
      breakPoint = checkResult.adjustedBreakPoint;
      pageHeight = breakPoint - currentY;

      console.log('调整分页以保护元素:', {
        originalBreakPoint: currentY + Math.min(safePageHeight, remainingHeight),
        adjustedBreakPoint: breakPoint,
        conflictElement: checkResult.conflictElement?.tagName,
        conflictElementClass: checkResult.conflictElement?.className,
      });

      // 确保页面高度不会太小
      if (pageHeight < safePageHeight * 0.2) {
        // 如果调整后页面太小，恢复原始分页点
        breakPoint = currentY + Math.min(safePageHeight, remainingHeight);
        pageHeight = breakPoint - currentY;
        console.log('页面太小，恢复原始分页点:', { breakPoint, pageHeight });
      }
    }

    // 添加重叠区域（除第一页外）
    const startY = currentY; // 直接使用当前位置，不减去overlap
    const actualHeight = pageHeight; // 直接使用计算的页面高度

    pagePositions.push({
      startY,
      height: Math.min(actualHeight, contentHeight - startY),
      pageIndex,
      breakPoint,
    });

    console.log(`智能分页第${pageIndex + 1}页:`, {
      startY,
      height: actualHeight,
      breakPoint,
      overlap: 0,
      protected: !checkResult.canFit ? '已调整' : '正常',
    });

    // 防止死循环：确保分页点前进
    if (breakPoint <= currentY) {
      console.warn('分页点未前进，强制前进避免死循环:', { currentY, breakPoint });
      breakPoint = currentY + Math.max(50, safePageHeight * 0.1);
    }

    currentY = breakPoint;
    pageIndex += 1;

    // 防止无限循环
    if (pageIndex > 100) {
      console.warn('分页数量过多，停止计算');
      break;
    }
  }

  return pagePositions;
}

export default function html2PDF(container, options = {}, callback) {
  const {
    margins: {
      top: marginTop = 0,
      right: marginRight = 0,
      bottom: marginBottom = 0,
      left: marginLeft = 0,
    },
    debug = false,
    beforeConvert,
    afterConvert,
  } = options;

  if (!container) {
    if (callback && typeof callback === 'function') {
      callback(null, error);
    }
    return;
  }

  beforeConvert?.();

  console.log('开始HTML转PDF处理');

  // 🔧 保存当前滚动位置
  const originalScrollTop = container.scrollTop;
  const originalScrollLeft = container.scrollLeft;
  console.log('保存原始滚动位置:', { scrollTop: originalScrollTop, scrollLeft: originalScrollLeft });

  // 🔧 临时重置滚动位置到顶部，确保html2canvas从正确位置开始截取
  container.scrollTop = 0;
  container.scrollLeft = 0;
  console.log('已重置滚动位置到顶部');

  // 添加调试边框，显示所有保护元素（仅在debug模式下）
  const protectedElements = debug ? addDebugBorders(container) : [];
  if (debug) {
    console.log('调试边框已添加，保护元素数量:', protectedElements.length);
  }

  // 根据设备像素密度动态设置scale
  const getOptimalScale = () => {
    const dpr = window.devicePixelRatio || 1;
    // 在高DPI屏幕上使用2，普通屏幕使用1
    return dpr > 1.5 ? 2 : 1;
  };

  // 设置html2canvas选项
  const html2canvasOptions = {
    allowTaint: true,
    useCORS: true,
    scale: options.canvasScale || getOptimalScale(),
    background: '#FFFFFF',
    logging: false,
    windowWidth: container.scrollWidth,
    windowHeight: container.scrollHeight,
    imageTimeout: 0,
    removeContainer: true,
    letterRendering: true,
  };

  // 开始转换
  html2Canvas(container, html2canvasOptions)
    .then((canvas) => {
      try {
        const contentWidth = canvas.width;
        const contentHeight = canvas.height;

        console.log('Canvas生成完成:', { width: contentWidth, height: contentHeight });

        // A4纸的尺寸[595.28, 841.89]，单位是pt
        const a4Width = 595.28;
        const a4Height = 841.89;

        // 考虑边距后的可用宽度和高度
        const availableWidth = a4Width - marginLeft - marginRight;
        const availableHeight = a4Height - marginTop - marginBottom;

        // 计算缩放比例，使内容适应A4纸宽度
        const scale = availableWidth / contentWidth;

        // 计算一页可以容纳的内容高度（以像素为单位）
        const pageHeightInPx = Math.floor(availableHeight / scale);

        // 使用安全的页面高度，避免文本截断
        const safePageHeightInPx = Math.floor(pageHeightInPx * 0.95);

        // 获取html2canvas的scale因子
        const canvasScale = html2canvasOptions.scale || 1;

        // 计算原始内容尺寸（除以scale因子）
        const originalContentHeight = contentHeight / canvasScale;
        const originalSafePageHeightInPx = safePageHeightInPx / canvasScale;

        console.log('尺寸计算:', {
          canvasScale,
          contentHeight,
          originalContentHeight,
          safePageHeightInPx,
          originalSafePageHeightInPx,
        });

        // 创建PDF实例
        const PDF = new JsPDF('p', 'pt', 'a4');

        // 获取所有元素的位置信息，用于智能分页
        const elements = getElementPositions(container);
        console.log('获取元素位置信息:', { elementsCount: elements.length });

        // 使用原始内容尺寸计算智能分页位置，确保与元素坐标系一致
        const pagePositions = calculateSmartPageBreaks(
          originalContentHeight,
          originalSafePageHeightInPx,
          elements,
        );
        console.log('智能分页计算完成:', { totalPages: pagePositions.length });

        // 分析保护元素在各页面的分布情况
        debug && analyzeElementPageDistribution(protectedElements, pagePositions);

        // 处理每一页
        pagePositions
          .filter((page) => page.height > 0 && page.startY < originalContentHeight)
          .forEach((page, index) => {
            const { startY, height } = page;

            // 将原始坐标转换为canvas坐标（乘以scale因子）
            const canvasStartY = startY * canvasScale;
            const canvasHeight = height * canvasScale;

            console.log(`处理第${index + 1}页:`, {
              originalStartY: startY,
              originalHeight: height,
              canvasStartY,
              canvasHeight,
              canvasSlice: `${canvasStartY} - ${canvasStartY + canvasHeight}`,
            });

            // 创建临时canvas用于当前页
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = contentWidth;
            tempCanvas.height = canvasHeight;

            // 将原canvas对应部分绘制到临时canvas
            const ctx = tempCanvas.getContext('2d');
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

            ctx.drawImage(
              canvas,
              0,
              canvasStartY,
              contentWidth,
              canvasHeight,
              0,
              0,
              contentWidth,
              canvasHeight,
            );

            // 将临时canvas转为图片数据
            const pageData = tempCanvas.toDataURL('image/jpeg', 1.0);

            // 清理临时Canvas，释放内存
            tempCanvas.width = 0;
            tempCanvas.height = 0;

            // 添加新页（除第一页外）
            if (index > 0) {
              PDF.addPage();
            }

            // 计算PDF中的正确高度
            const pdfHeight = canvasHeight * scale;

            // 添加图像到PDF
            PDF.addImage(
              pageData,
              'JPEG',
              marginLeft,
              marginTop,
              availableWidth,
              Math.min(pdfHeight, availableHeight),
            );
          });

        // 生成PDF blob并通过回调返回
        const data = PDF.output('blob');
        console.log('PDF生成完成:', { size: `${(data.size / 1024).toFixed(2)}KB`, pages: pagePositions.length });

        if (callback && typeof callback === 'function') {
          callback(data);
        }

        // 🔧 恢复原始滚动位置
        container.scrollTop = originalScrollTop;
        container.scrollLeft = originalScrollLeft;
        console.log('已恢复原始滚动位置:', { scrollTop: originalScrollTop, scrollLeft: originalScrollLeft });

        afterConvert?.();

        return data;
      } catch (error) {
        console.error('处理PDF时发生错误:', error);
        throw error;
      }
    })
    .catch((error) => {
      // 🔧 即使出错也要恢复原始滚动位置
      container.scrollTop = originalScrollTop;
      container.scrollLeft = originalScrollLeft;
      console.log('出错时恢复原始滚动位置:', { scrollTop: originalScrollTop, scrollLeft: originalScrollLeft });

      afterConvert?.();
      console.error('生成PDF时发生错误:', error);
      throw error;
    });
}
